import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AdminPanel = () => {
  const { accountStore, viewAsStore } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(accountStore?.businessId ? String(accountStore.businessId) : null);
  const [sites, setSites] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [siteUsers, setSiteUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newBusinessName, setNewBusinessName] = useState('');
  const [newBusinessSlug, setNewBusinessSlug] = useState('');
  const [showCreateBusinessForm, setShowCreateBusinessForm] = useState(false);
  const [editingBusinessName, setEditingBusinessName] = useState('');
  const [activeBusinessAction, setActiveBusinessAction] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'site_user' });

  useEffect(() => {
    if (accountStore?.role === 'app_admin') {
      fetchBusinesses();
    } else if (accountStore?.businessId) {
      setSelectedBusinessId(String(accountStore.businessId));
    }
  }, [accountStore]);

  useEffect(() => {
    if (!selectedBusinessId) {
      setLoading(false);
      return;
    }

    fetchBusinessData(selectedBusinessId);
  }, [selectedBusinessId]);

  const fetchBusinesses = async () => {
    try {
      const response = await api.get('/admin/businesses');
      setBusinesses(response.data.businesses);
      if (!selectedBusinessId && response.data.businesses.length > 0) {
        setSelectedBusinessId(String(response.data.businesses[0].id));
      }
    } catch (err) {
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessData = async (businessId) => {
    try {
      setLoading(true);
      const [businessRes, sitesRes, usersRes, itemsRes] = await Promise.all([
        api.get(`/admin/businesses/${businessId}`),
        api.get(`/admin/businesses/${businessId}/sites`),
        api.get(`/admin/businesses/${businessId}/users`),
        api.get(`/admin/businesses/${businessId}/items`)
      ]);
      setBusiness(businessRes.data.business);
      setEditingBusinessName(businessRes.data.business.name || '');
      setActiveBusinessAction(null);
      setShowCreateBusinessForm(false);
      setSites(sitesRes.data.sites);
      setAdminUsers(usersRes.data.users.filter((user) => user.role === 'business_admin'));
      setSiteUsers(usersRes.data.users.filter((user) => user.role === 'site_user'));
      setItems(itemsRes.data.items);
    } catch (err) {
      setError('Failed to load business data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/admin/businesses', {
        name: newBusinessName,
        slug: newBusinessSlug || undefined
      });
      setBusinesses([...businesses, response.data.business]);
      setNewBusinessName('');
      setNewBusinessSlug('');
      setShowCreateBusinessForm(false);
      setSelectedBusinessId(response.data.business.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create business');
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm('Delete this business? This will remove associated stores and stock data.')) {
      return;
    }

    try {
      await api.delete(`/admin/businesses/${businessId}`);
      const remaining = businesses.filter((business) => business.id !== businessId);
      setBusinesses(remaining);
      setSelectedBusinessId(remaining[0]?.id ? String(remaining[0].id) : null);
    } catch (err) {
      alert('Failed to delete business');
    }
  };

  const handleOpenBusiness = (businessId) => {
    setError('');
    setLoading(true);
    setSites([]);
    setAdminUsers([]);
    setSiteUsers([]);
    setItems([]);
    setBusiness(null);
    setActiveBusinessAction(null);
    setShowCreateBusinessForm(false);
    setSelectedBusinessId(String(businessId));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/businesses/${selectedBusinessId}/users`, newUser);
      setNewUser({ name: '', email: '', password: '', role: 'site_user' });
      fetchBusinessData(selectedBusinessId);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateBusiness = async (e) => {
    e.preventDefault();

    try {
      const response = await api.patch(`/admin/businesses/${selectedBusinessId}`, {
        name: editingBusinessName
      });

      setBusiness(response.data.business);
      setBusinesses((currentBusinesses) =>
        currentBusinesses.map((entry) => (
          String(entry.id) === String(response.data.business.id) ? response.data.business : entry
        ))
      );
      setActiveBusinessAction(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update business');
    }
  };

  const selectedBusiness = businesses.find((business) => String(business.id) === String(selectedBusinessId));
  const activeBusinessName = business?.name || selectedBusiness?.name || 'Selected Business';

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Admin Panel</h2>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {accountStore?.role === 'app_admin' && (
          <div className="card mb-3">
            <div className="card-header" style={{ marginBottom: '0.75rem' }}>
              <h3 className="card-title">Businesses</h3>
              <button
                type="button"
                className="btn btn-primary btn-small"
                onClick={() => setShowCreateBusinessForm((current) => !current)}
                style={{ width: 'auto', flex: '0 0 auto' }}
              >
                {showCreateBusinessForm ? 'Close' : 'Create Business'}
              </button>
            </div>

            {showCreateBusinessForm && (
              <form onSubmit={handleCreateBusiness} className="mb-3" style={{ background: 'var(--pink-light)', padding: '1rem', borderRadius: '8px' }}>
                <div className="form-group">
                  <label className="form-label">Business Name</label>
                  <input
                    className="form-input"
                    value={newBusinessName}
                    onChange={(e) => setNewBusinessName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Slug</label>
                  <input
                    className="form-input"
                    value={newBusinessSlug}
                    onChange={(e) => setNewBusinessSlug(e.target.value)}
                    placeholder="optional"
                  />
                </div>
                <button type="submit" className="btn btn-primary">Create Business</button>
              </form>
            )}

            {businesses.length > 0 ? (
              <>
            {businesses.map((business) => (
              <div key={business.id} className="list-item">
                <div className="list-item-content">
                  <div className="list-item-title">{business.name}</div>
                  <div className="list-item-subtitle">Slug: {business.slug || 'n/a'}</div>
                </div>
                <div className="list-item-actions">
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => handleOpenBusiness(business.id)}>
                    Open
                  </button>
                  <button className="btn btn-danger btn-small" onClick={() => handleDeleteBusiness(business.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
              </>
            ) : (
              <div className="text-center" style={{ padding: '1rem', color: 'var(--text-light)' }}>
                No businesses yet.
              </div>
            )}
          </div>
        )}

        {selectedBusinessId && (
          <>
            <div className="card mb-3">
              <div className="card-header">
                <h3 className="card-title">
                  {activeBusinessName}
                </h3>
              </div>
              <div className="flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-secondary btn-small" onClick={() => navigate(`/manage-items?businessId=${selectedBusinessId}`)}>
                  Manage Items
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => setActiveBusinessAction(activeBusinessAction === 'rename' ? null : 'rename')}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => setActiveBusinessAction(activeBusinessAction === 'create-user' ? null : 'create-user')}
                >
                  Create User
                </button>
              </div>

              {activeBusinessAction === 'rename' && (
                <form onSubmit={handleUpdateBusiness} className="mb-3">
                  <div className="form-group">
                    <label className="form-label">Business Name</label>
                    <div className="flex gap-2">
                      <input
                        className="form-input"
                        value={editingBusinessName}
                        onChange={(e) => setEditingBusinessName(e.target.value)}
                        disabled={!selectedBusinessId}
                        required
                      />
                      <button type="submit" className="btn btn-primary" disabled={!selectedBusinessId} style={{ width: 'auto', flex: '0 0 auto' }}>
                        Save Name
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {activeBusinessAction === 'create-user' && (
                <form onSubmit={handleCreateUser} className="card mb-3" style={{ background: 'var(--pink-light)' }}>
                  <h3 className="mb-2">Create User</h3>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input className="form-input" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-input" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <input type="password" className="form-input" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                      <option value="site_user">Site User</option>
                      <option value="business_admin">Business Admin</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary">Create User</button>
                </form>
              )}

              <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                <div>
                  <strong>Sites</strong>
                  <div>{sites.length} site(s)</div>
                </div>
                <div>
                  <strong>Users</strong>
                  <div>{adminUsers.length} admin, {siteUsers.length} site</div>
                </div>
                <div>
                  <strong>Items</strong>
                  <div>{items.length} item(s)</div>
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <h3 className="mb-2">Sites</h3>
              {loading ? (
                <div className="loading"><div className="spinner"></div></div>
              ) : sites.length === 0 ? (
                <div className="text-center" style={{ padding: '1rem', color: 'var(--text-light)' }}>No sites found.</div>
              ) : (
                sites.map((site) => (
                  <div key={site.id} className="list-item">
                    <div className="list-item-content">
                      <div className="list-item-title">{site.name}</div>
                      <div className="list-item-subtitle">{site.email}</div>
                    </div>
                    <div className="list-item-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={() => {
                          viewAsStore(site);
                          navigate('/');
                        }}
                      >
                        View as Site User
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="card mb-3">
              <h3 className="mb-2">Admin Users</h3>
              {adminUsers.length === 0 ? (
                <div className="text-center" style={{ padding: '1rem', color: 'var(--text-light)' }}>No admin users found.</div>
              ) : (
                adminUsers.map((user) => (
                  <div key={user.id} className="list-item">
                    <div className="list-item-content">
                      <div className="list-item-title">{user.name}</div>
                      <div className="list-item-subtitle">{user.email} • {user.role.replace('_', ' ')}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="card mb-3">
              <h3 className="mb-2">Items</h3>
              {items.length === 0 ? (
                <div className="text-center" style={{ padding: '1rem', color: 'var(--text-light)' }}>No items found.</div>
              ) : (
                items.map((item) => (
                  <div key={item.id} className="list-item">
                    <div className="list-item-content">
                      <div className="list-item-title">{item.name}</div>
                      <div className="list-item-subtitle">{item.code || 'No code'} • {item.unit_type}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;